import React, { useState, useRef, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { SectorData } from "../../data/sectorData";
import { ArrowUp, ArrowDown, Minus } from "lucide-react";

interface SectorPerformanceChartProps {
  data: SectorData["sectors"];
  date: string;
  previousData?: SectorData["sectors"];
}

const SectorPerformanceChart: React.FC<SectorPerformanceChartProps> = ({
  data,
  date,
  previousData,
}) => {
  const [hoveredBar, setHoveredBar] = useState<number | null>(null);
  const [animatingBars, setAnimatingBars] = useState<Set<number>>(new Set());
  const prevDataRef = useRef(data);

  const NEUTRAL_THRESHOLD = 0.05;

  useEffect(() => {
    const newAnimatingBars = new Set(data.map((_, index) => index));
    setAnimatingBars(newAnimatingBars);

    const timer = setTimeout(() => {
      setAnimatingBars(new Set());
    }, 700);

    prevDataRef.current = data;
    return () => clearTimeout(timer);
  }, [data]);

  const enrichedData = data.map((item, index) => {
    const previousValue = previousData?.[index]?.change || 0;
    const netChange = item.change - previousValue;
    return {
      ...item,
      previousValue,
      netChange,
    };
  }).sort((a, b) => b.change - a.change);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0].payload;
    const currentChange = data.change;
    const previousChange = data.previousValue;
    const netChange = data.netChange;

    const isPositive = currentChange > NEUTRAL_THRESHOLD;
    const isNegative = currentChange < -NEUTRAL_THRESHOLD;

    return (
      <div className="bg-black/90 backdrop-blur-md border border-white/10 rounded-lg shadow-xl p-3 font-mono">
        <p className="text-white/90 mb-2 font-medium">{label}</p>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-white/60">Current:</span>
            <span className={`font-medium ${isPositive ? 'text-green-400' :
                isNegative ? 'text-red-400' :
                  'text-gray-400'
              }`}>
              {currentChange.toFixed(2)}%
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-white/60">Previous:</span>
            <span className="text-white/80">{previousChange.toFixed(2)}%</span>
          </div>
          <div className="flex items-center gap-2 mt-1 pt-1 border-t border-white/10">
            <span className="text-white/60">Change:</span>
            <span className={`font-medium ${netChange > 0 ? 'text-green-400' :
                netChange < 0 ? 'text-red-400' :
                  'text-gray-400'
              }`}>
              {netChange > 0 ? '+' : ''}{netChange.toFixed(2)}%
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full">
      <div className="mb-4 flex justify-between items-center">
        <h2 className="text-xl font-medium text-white/90 font-mono">Momentum Analysis-Sector</h2>
        {/* <p className="text-sm text-white/60 font-mono">Last Updated: {date}</p> */}
      </div>
      <div className="w-full h-[400px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={enrichedData}
            margin={{ top: 20, right: 30, left: 20, bottom: 50 }}
            layout="horizontal"
            barSize={30}
            className="[&_.recharts-cartesian-grid-horizontal_line]:stroke-white/10 [&_.recharts-cartesian-grid-vertical_line]:stroke-white/10"
            onMouseMove={(data) => {
              if (data.activeTooltipIndex !== undefined) {
                setHoveredBar(data.activeTooltipIndex);
              }
            }}
            onMouseLeave={() => setHoveredBar(null)}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.1)" />
            <XAxis
              dataKey="name"
              type="category"
              tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 12, fontFamily: 'monospace' }}
              interval={0}
              angle={-45}
              textAnchor="end"
              height={70}
              stroke="rgba(255,255,255,0.2)"
            />
            <YAxis
              type="number"
              domain={[-4, 4]}
              tickCount={9}
              tickFormatter={(value) => `${value}%`}
              tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 12, fontFamily: 'monospace' }}
              stroke="rgba(255,255,255,0.2)"
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.05)' }} />
            <ReferenceLine y={0} stroke="rgba(255,255,255,0.2)" />

            <Bar
              dataKey="previousValue"
              animationDuration={600}
              animationEasing="ease-in-out"
              className="opacity-30"
            >
              {enrichedData.map((entry, index) => (
                <Cell
                  key={`ghost-${index}`}
                  fill="#666"
                  className="rounded-t-lg"
                />
              ))}
            </Bar>

            <Bar
              dataKey="change"
              animationDuration={600}
              animationEasing="ease-in-out"
            >
              {enrichedData.map((entry, index) => {
                const isPositive = entry.change > NEUTRAL_THRESHOLD;
                const isNegative = entry.change < -NEUTRAL_THRESHOLD;
                const isNeutral = Math.abs(entry.change) <= NEUTRAL_THRESHOLD;
                const isHovered = index === hoveredBar;
                const isAnimating = animatingBars.has(index);

                let fillId = isPositive
                  ? "positiveGradient"
                  : isNegative
                    ? "negativeGradient"
                    : "neutralGradient";

                return (
                  <Cell
                    key={`cell-${index}`}
                    fill={`url(#${fillId})`}
                    className={`
                      rounded-t-lg transition-all duration-500 ease-in-out
                      ${isHovered ? 'opacity-100' : 'opacity-90'}
                      ${isNeutral ? 'animate-pulse' : ''}
                      ${isAnimating ? 'animate-flash' : ''}
                    `}
                  >
                    {isHovered && (
                      <g transform={`translate(${15}, ${entry.change >= 0 ? -20 : 20})`}>
                        {isPositive && <ArrowUp className="text-green-400" size={16} />}
                        {isNegative && <ArrowDown className="text-red-400" size={16} />}
                        {isNeutral && <Minus className="text-gray-400" size={16} />}
                      </g>
                    )}
                  </Cell>
                );
              })}
            </Bar>

            <defs>
              <linearGradient id="positiveGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#4ade80" stopOpacity={0.9} />
                <stop offset="50%" stopColor="#22c55e" stopOpacity={0.95} />
                <stop offset="100%" stopColor="#16a34a" stopOpacity={1} />
              </linearGradient>

              <linearGradient id="negativeGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f87171" stopOpacity={0.9} />
                <stop offset="50%" stopColor="#ef4444" stopOpacity={0.95} />
                <stop offset="100%" stopColor="#dc2626" stopOpacity={1} />
              </linearGradient>

              <linearGradient id="neutralGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#94a3b8" stopOpacity={0.7} />
                <stop offset="100%" stopColor="#64748b" stopOpacity={0.8} />
              </linearGradient>
            </defs>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default SectorPerformanceChart;
